import { PRODUCT_TYPE } from 'configs/constants';
import envConfig from 'configs/envConfig';
import { TSupportedFeature } from 'typings';

export const FREE_TRIAL_DURATION = Number(envConfig('freeTrialDays') ?? 7);
export const FREE_TRIAL_DURATION_TRAIL_CAMERA = Number(
  envConfig('freeTrialDaysTrailCamera') ?? 10
);
/**
 *
 * A data subscription always grants 500; a combo grants 500 (Solo) or 600
 * (Enhanced / Premium), so its heading quotes the highest tier as "up to".
 *
 */
export const FREE_TRIAL_DATA_MB = 500;
export const FREE_TRIAL_COMBO_MAX_DATA_MB = 600;

export const PROSECURITY_COUNTRIES = ['US', 'CA'];

export const maxCameraLimit = 5; // maximum no of camera allowed by any high tier plan

export const TOKEN_REFRESH_THRESHOLD = 5 * 60; // 5 minutes => calls refresh token logic when access token is about to expire in 5 minutes

// A pool balance under this is treated as "no data at all": the leftover is not
// usable by the customer, so enabling auto recharge has to charge them now to
// activate the chosen plan instead of waiting for the balance to run out.
export const LOW_POOL_DATA_THRESHOLD_MB = 50;

export const PLAN_DETAILS = {
  MONTHLY: {
    SOLO: {
      LABEL: 'Monthly',
      CYCLE: 1
    },
    ENHANCED: {
      LABEL: 'Enhanced',
      CYCLE: 1
    },
    PREMIUM: {
      LABEL: 'Premium',
      CYCLE: 1
    }
  },
  YEARLY: {
    SOLO: {
      LABEL: 'Monthly',
      CYCLE: 12
    },
    ENHANCED: {
      LABEL: 'Enhanced',
      CYCLE: 12
    },
    PREMIUM: {
      LABEL: 'Premium',
      CYCLE: 12
    }
  }
};

export enum PairingScenario {
  FIRST_TIME = 'FIRST_TIME',
  SUBSEQUENT_ONE_CAMERA = 'SUBSEQUENT_ONE_CAMERA',
  SUBSEQUENT_MULTI_CAMERA = 'SUBSEQUENT_MULTI_CAMERA'
}

// Suggested camera names offered on the Camera Naming screen, per product.
// The first entry is what the name field is pre-filled with.
export interface CommonNameOption {
  value: string;
  labelKey: string;
}

export const COMMON_NAME_OPTIONS_BY_PRODUCT_TYPE: Record<
  string,
  CommonNameOption[]
> = {
  [PRODUCT_TYPE.BIRD]: [
    { value: 'Garden Feeder', labelKey: 'commonNameGardenFeeder' },
    { value: 'Patio Feeder', labelKey: 'commonNamePatioFeeder' },
    { value: 'Backyard', labelKey: 'commonNameBackyard' },
    { value: 'Window Feed', labelKey: 'commonNameWindowFeed' },
    { value: 'Bird Station', labelKey: 'commonNameBirdStation' },
    { value: 'Tree Feeder', labelKey: 'commonNameTreeFeeder' }
  ],
  [PRODUCT_TYPE.PET]: [
    { value: 'Pet Room', labelKey: 'commonNamePetRoom' },
    { value: 'Dog Cam', labelKey: 'commonNameDogCam' },
    { value: 'Cat Cam', labelKey: 'commonNameCatCam' },
    { value: 'Play Area', labelKey: 'commonNamePlayArea' },
    { value: 'Pet Bed', labelKey: 'commonNamePetBed' },
    { value: 'Food Station', labelKey: 'commonNameFoodStation' }
  ],
  [PRODUCT_TYPE.TRAIL]: [
    { value: 'Food Plot', labelKey: 'commonNameFoodPlot' },
    { value: 'North Trail', labelKey: 'commonNameNorthTrail' },
    { value: 'Deer Stand', labelKey: 'commonNameDeerStand' },
    { value: 'Creek Cross', labelKey: 'commonNameCreekCross' },
    { value: 'Back Woods', labelKey: 'commonNameBackWoods' },
    { value: 'Game Trail', labelKey: 'commonNameGameTrail' }
  ],
  [PRODUCT_TYPE.BABY]: [
    { value: 'Nursery', labelKey: 'commonNameNursery' },
    { value: 'Crib', labelKey: 'commonNameCrib' },
    { value: 'Baby Room', labelKey: 'commonNameBabyRoom' },
    { value: 'Playroom', labelKey: 'commonNamePlayroom' },
    { value: 'Bassinet', labelKey: 'commonNameBassinet' },
    { value: 'Baby Bed', labelKey: 'commonNameBabyBed' }
  ]
};

export const DEFAULT_COMMON_NAME_OPTIONS: CommonNameOption[] = [
  { value: 'Living Room', labelKey: 'commonNameLivingRoom' },
  { value: 'Front Door', labelKey: 'commonNameFrontDoor' },
  { value: 'Kitchen', labelKey: 'commonNameKitchen' },
  { value: 'Outside', labelKey: 'commonNameOutside' },
  { value: 'Porch', labelKey: 'commonNamePorch' },
  { value: 'Backyard', labelKey: 'commonNameBackyard' }
];

// Hub flow (/addHubInfo). Hub names resolve against `views.addHubInfo`.
export const HUB_COMMON_NAME_OPTIONS: CommonNameOption[] = [
  { value: 'Home Hub', labelKey: 'commonNameHomeHub' },
  { value: 'Main Hub', labelKey: 'commonNameMainHub' },
  { value: 'Office Hub', labelKey: 'commonNameOfficeHub' },
  { value: 'Hub Station', labelKey: 'commonNameHubStation' }
];

// Suggestions for the cameras attached to a hub. These resolve against
// `views.addCameraInfo`, so they reuse the existing camera-name translations.
// Each camera is pre-filled with the first one no sibling camera has taken.
export const HUB_CAMERA_COMMON_NAME_OPTIONS: CommonNameOption[] = [
  { value: 'Front Door', labelKey: 'commonNameFrontDoor' },
  { value: 'Driveway', labelKey: 'commonNameDriveway' },
  { value: 'Living Room', labelKey: 'commonNameLivingRoom' },
  { value: 'Kitchen', labelKey: 'commonNameKitchen' },
  { value: 'Backyard', labelKey: 'commonNameBackyard' },
  { value: 'Porch', labelKey: 'commonNamePorch' }
];

/* IMPORTANT NOTE

DO NOT CHANGE ROUTES which are used by App or Backend It will break the flow

*/
export const ROUTES = {
  // Core Pages
  LAUNCH_APP: '/launchMobileApp',
  LOGIN: '/login',
  APP_BRIDGE: '/app-bridge', // * USED BY APP *
  PRIVACY_POLICY: '/privacy-policy', // * USED BY APP *
  TERMS_OF_SERVICE: '/terms-of-service', // * USED BY APP *
  NOT_FOUND: '*',

  // Account Deletion
  REQUEST_DELETE: '/requestDeleteServices', // * USED BY Backend/App *
  VERIFY_DELETE: '/verifyDeleteServices', // * USED BY Backend/App *

  // Password Reset — the landing page for the link in the backend's reset email.
  // The email appends `?token=<token>&lang=<code>`.
  RESET_PASSWORD: '/reset-password', // * USED BY Backend *

  // Core Features
  CAMERA_LIMIT_REACHED: '/camera-limit-reached',
  FAQ: '/frequently-asked-questions',
  SUPPORT: '/help-and-support',
  APP_QR_SUPPORT: '/support',
  CANCELLATION_REFUND: '/cancellation-refund-policy',

  // Dashboard
  DASHBOARD: '/dashboard',

  // Subscriptions
  // Canonical Home Security routes. Home is a product like Bird/Pet/Trail/
  // Baby, so its screens live under /home/* alongside them — which is also
  // what RouteProductTheme reads to tint the page.
  HOME_BUY_SUBSCRIPTION: '/home/buySubscription',
  HOME_SUBSCRIPTION_UPGRADE: '/home/subscriptions/:id/upgrade',
  // Multiple Plan Subscriptions (FCM-2916). The remove flow's later steps
  // (choose cameras, scheduled) are `?step=` views of the same route.
  HOME_ADD_PLAN: '/home/subscriptions/:id/add-plan', // * USED BY APP *
  HOME_REMOVE_PLAN: '/home/subscriptions/:id/remove-plan', // * USED BY APP *

  // Kept as redirects to the two above: the native app deep-links these, so
  // they must keep resolving for every build already in the wild.
  BUY_SUBSCRIPTION: '/buySubscription', // * USED BY APP *
  REVIEW_PURCHASE: '/subscription/:id/upgrade/review',
  REVIEW_DOWNGRADE: '/reviewDowngrade',
  PAYMENT_SUCCESS: '/payment-success', // * USED BY BACKEND *
  SUBSCRIPTION_LIST: '/subscriptions', // * USED BY APP *
  SUBSCRIPTION_DETAIL: '/subscription/:id',
  SUBSCRIPTION_UPGRADE: '/subscription/:id/upgrade', // * USED BY APP *
  UPGRADE_BASIC: '/upgradeBanner',
  BASE_PLANS_AUTO_TOP_UP: '/base-plans-auto-top-up',

  // Data Plans (data is no longer tied to a product, so no `/home` prefix).
  // * USED BY APP * to open the data webviews.
  DATA_SUBSCRIPTION: '/data/subscription/buy',
  UPGRADE_DATA_SUBSCRIPTION: '/data/subscription/:id/upgrade',
  ONE_TIME_DATA_PURCHASE: '/data/one-time',
  DATA_AUTO_TOP_UP: '/data/auto-top-up',
  DATA_AUTO_TOP_UP_PAYMENT: '/data/auto-top-up/payment',
  DATA_ONLY_UPSELL: '/data-upsell',

  // Legacy data routes kept only to redirect older app builds to the routes
  // above. Do not navigate to these from new code.
  LEGACY_DATA_SUBSCRIPTION: '/home/subscriptions/buy-data',
  LEGACY_UPGRADE_DATA_SUBSCRIPTION: '/home/subscriptions/:id/upgrade-data',
  LEGACY_ONE_TIME_DATA_PURCHASE: '/home/one-time/buy-data',
  LEGACY_DATA_AUTO_TOP_UP: '/home/data-auto-top-up',
  LEGACY_DATA_AUTO_TOP_UP_PAYMENT: '/home/data-auto-top-up/payment',

  // Bird Feeder Flow
  BIRD_UPSELL: '/bird/upsell',
  BIRD_SKIP_TRIAL: '/bird/skipTrial',
  BIRD_WHY_SKIP_TRIAL: '/bird/whySkipTrial',

  // * USED BY APP *
  BIRD_BUY_SUBSCRIPTION: '/bird/buySubscription', // * USED BY APP *
  BIRD_SUBSCRIPTION_DETAIL: '/bird/subscription/:id', // * USED BY APP *
  BIRD_SUBSCRIPTION_UPGRADE: '/bird/subscription/:id/upgrade', // * USED BY APP *

  // Pet Camera Flow
  PET_UPSELL: '/pet/upsell',
  PET_SKIP_TRIAL: '/pet/skipTrial',
  PET_WHY_SKIP_TRIAL: '/pet/whySkipTrial',

  // * USED BY APP *
  PET_BUY_SUBSCRIPTION: '/pet/buySubscription', // * USED BY APP *
  PET_SUBSCRIPTION_DETAIL: '/pet/subscription/:id', // * USED BY APP *
  PET_SUBSCRIPTION_UPGRADE: '/pet/subscription/:id/upgrade', // * USED BY APP *

  BABY_UPSELL: '/baby/upsell',
  BABY_SKIP_TRIAL: '/baby/skipTrial',
  BABY_WHY_SKIP_TRIAL: '/baby/whySkipTrial',

  // * USED BY APP *
  BABY_BUY_SUBSCRIPTION: '/baby/buySubscription', // * USED BY APP *
  BABY_SUBSCRIPTION_DETAIL: '/baby/subscription/:id', // * USED BY APP *
  BABY_SUBSCRIPTION_UPGRADE: '/baby/subscription/:id/upgrade', // * USED BY APP *

  // Trail Cam Flow
  TRAIL_CAM_UPSELL: '/trail/upsell',
  TRAIL_CAM_SKIP_TRIAL: '/trail/skipTrial',
  TRAIL_CAM_WHY_SKIP_TRIAL: '/trail/whySkipTrial',

  // * USED BY APP *
  TRAIL_CAM_BUY_SUBSCRIPTION: '/trail/buySubscription', // * USED BY APP *
  TRAIL_CAM_SUBSCRIPTION_DETAIL: '/trail/subscription/:id', // * USED BY APP *
  TRAIL_CAM_SUBSCRIPTION_UPGRADE: '/trail/subscription/:id/upgrade', // * USED BY APP *

  // Payment * USED BY APP *
  PAYMENT_METHODS: '/paymentMethods', // * USED BY APP *
  RETRY_PAYMENT: '/retryPayment', // * USED BY APP *

  // checkout
  CHECKOUT_SESSION: '/checkoutSession',

  // Billing
  BILLING: '/billing', // * USED BY APP *
  BILLINGS: '/billings', // * USED BY APP *

  // Plans & Offers
  PROMOTION: '/home/upsell',

  // Camera * USED BY APP *
  ADD_CAMERA_INFO: '/addCameraInfo', // * USED BY APP *
  // Hub + its cameras. Steps are `?step=` views of this one route.
  ADD_HUB_INFO: '/addHubInfo', // * USED BY APP *

  // Trial Skipping
  SKIP_TRIAL: '/home/skipTrial',
  WHY_SKIP_TRIAL: '/home/whySkipTrial'
};

export const getRoute = (
  routeKey: string,
  params?: Record<string, string | number>
): string => {
  let path = routeKey;

  // Replace any :params in route
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      path = path.replace(`:${key}`, String(value));
    });
  }

  return path;
};

export const SUBSCRIPTION_SERVICE = 'piggybank';

export const APP_PRO_MONITORING_SCREEN = 'ProMonitoringScreen';

// These are set by app when it opens webview
export enum QUERY_PARAMS {
  POST_PAIRING_FLOW = 'post_pairing_flow',
  DEVICE_ID = 'device_id',
  SESSION_ID = 'session_id',
  USER_PATH = 'user_path',
  URL_PATH = 'url_path',
  REDIRECT_PATH = 'redirect',
  // Set by the billing page when it routes into /retryPayment (update card /
  // re-subscribe / different card). On success the retry screen returns here
  // instead of closing the webview. Value: 'billing'.
  RETURN_TO = 'return_to',
  // Which product the app was showing when it opened the webview — the
  // PRODUCT_TYPE enum's own values (HomeSecurity/Bird/Pet/Trail/Baby).
  // Persisted to sessionStorage at /app-bridge (see AppBridgePage) and read
  // by PrivateRoute as a theming fallback for routes whose URL has no
  // product segment of its own (data/payment/billing/camera routes) — see
  // productFromType/productSegmentFromPath in themes/tokens/productTheme.ts.
  // Named _QUERY to avoid shadowing the PRODUCT_TYPE object above.
  PRODUCT_TYPE_QUERY = 'product_type'
}

// These are keys for items stored in local storage
export enum LS_KEYs {
  DEVICE_ID_PAIRED = 'deviceIdBeingPaired'
}

export function isPairingFlow(): boolean {
  try {
    const value = localStorage.getItem(LS_KEYs.DEVICE_ID_PAIRED);

    // If did is present in local storage → available
    return typeof value === 'string' && value.trim().length > 0;
  } catch {
    return false;
  }
}

export const isProMonitoringEnabled = (features: TSupportedFeature[]) =>
  features.some((f) => f?.id === 'monitoring');

export enum GRACE_PERIOD_BLOCK_ERROR_CODE {
  GRACE_UPGRADE_BLOCK = 'Subscription_GraceUpgradeBlock',
  GRACE_DOWNGRADE_BLOCK = 'Subscription_GraceDowngradeBlock'
}
